import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import { useState } from "react";

import { applySetupCommand, newIdempotencyKey, setupErrorCode, setupErrorMessage } from "@/libs/tournamentSetupApi";
import type { TournamentSetupView } from "@/libs/tournamentSetupApi";

import { LockedField } from "./locked-field";
import { CheckIcon, DenseButton, LockIcon, Note, SectionHeading, SetupSurface, tone, WarningIcon } from "./setup/ui";

/** "Open registration for …?" with what locks after the first entry (F11).
 * Existing court bookings in the play hours stay; opening holds the courts
 * around them, or the organizer changes courts first. */
export function OpenRegistrationDialog({
  view,
  open,
  onClose,
  onOpened,
  onChangeCourts,
  onConflict,
}: {
  view: TournamentSetupView;
  open: boolean;
  onClose: () => void;
  onOpened: (next: TournamentSetupView) => void;
  onChangeCourts?: () => void;
  /** A booking landed since this preview: refresh so the list is current. */
  onConflict?: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preview = view.open_registration;
  const conflicts = preview.conflicts ?? [];
  const confirm = async () => {
    setPending(true);
    setError(null);
    try {
      const next = await applySetupCommand(view.tournament_id, {
        command: "open_registration",
        idempotency_key: newIdempotencyKey(),
        expected_revision: view.revision,
        accept_conflicts: conflicts.length > 0,
      });
      onOpened(next);
    } catch (cause) {
      setError(setupErrorMessage(cause, "Registration couldn’t open. Try again."));
      const code = setupErrorCode(cause);
      if (code === "SETUP_HOLD_CONFLICTS" || code === "SETUP_HOLD_RACE") onConflict?.();
    } finally {
      setPending(false);
    }
  };
  return (
    <Dialog aria-labelledby="or-title" maxWidth={false} onClose={onClose} open={open} slotProps={{ paper: { sx: { width: 960, maxWidth: "calc(100% - 32px)", borderRadius: "12px", mt: "50px" } } }} sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}>
      <SetupSurface>
        <Box sx={{ p: "14px 18px", borderBottom: `1px solid ${tone.divider}` }}>
          <Box component="b" id="or-title" sx={{ fontSize: 15 }}>
            Open registration for {view.name}?
          </Box>
          <Box sx={{ fontSize: 12.5, color: tone.muted, mt: "3px" }}>{preview.summary_line}</Box>
        </Box>
        {preview.warnings.length ? (
          <Note icon={<WarningIcon />} role="status" sx={{ m: "12px 18px 0" }} tone="amber">
            <span>
              <b>{preview.warnings[0]}</b> {preview.warnings.slice(1).join(" ")}
            </span>
          </Note>
        ) : null}
        {conflicts.length ? (
          <Note icon={<WarningIcon />} sx={{ m: "8px 18px 0", alignItems: "flex-start" }} tone="amber">
            <Box>
              <b>{preview.conflicts_line}</b>
              <Box component="ul" sx={{ m: "4px 0 0", pl: 2.5 }}>
                {conflicts.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </Box>
            </Box>
          </Note>
        ) : null}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 300px" }, gap: "18px", p: "14px 18px" }}>
          <Box>
            <SectionHeading sx={{ mb: "6px" }}>Locked after the first entry</SectionHeading>
            {preview.locks_after_first_entry.map((line) => (
              <Box key={line.title} sx={{ display: "flex", gap: 1, alignItems: "flex-start", py: "6px", borderTop: `1px solid ${tone.divider}`, fontSize: 12.5 }}>
                <Box component="span" sx={{ color: tone.muted, mt: "2px" }}>
                  <LockIcon />
                </Box>
                <div>
                  <b>{line.title}</b>
                  {line.detail ? (
                    <Box component="span" sx={{ display: "block", color: tone.subtle }}>
                      {line.detail}
                    </Box>
                  ) : null}
                </div>
              </Box>
            ))}
          </Box>
          <Box>
            <SectionHeading sx={{ mb: "6px" }}>You can still change</SectionHeading>
            {preview.still_editable.map((line) => (
              <Box key={line.title} sx={{ display: "flex", gap: 1, alignItems: "flex-start", py: "6px", borderTop: `1px solid ${tone.divider}`, fontSize: 12.5 }}>
                <Box component="span" sx={{ mt: "2px" }}>
                  <CheckIcon color={tone.brandText} size={14} />
                </Box>
                <div>
                  <b>{line.title}</b>
                  {line.detail ? (
                    <Box component="span" sx={{ display: "block", color: tone.subtle }}>
                      {line.detail}
                    </Box>
                  ) : null}
                </div>
              </Box>
            ))}
          </Box>
          <Box sx={{ position: "relative", minHeight: 150 }}>
            <SectionHeading sx={{ mb: "6px" }}>How a locked field looks</SectionHeading>
            <LockedField id="lk-fee" label={preview.locked_sample_label} reason={preview.locked_sample_reason} value={preview.locked_sample_value} />
            <Box sx={{ mt: 1, p: "10px 12px", borderRadius: "8px", bgcolor: tone.ink, color: tone.onInk, fontSize: 12.5, lineHeight: 1.45 }}>
              {preview.locked_sample_reason}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", p: "12px 18px", borderTop: `1px solid ${tone.divider}`, bgcolor: tone.page }}>
          {error ? (
            <Box role="alert" sx={{ fontSize: 12.5, color: tone.badText }}>
              {error}
            </Box>
          ) : (
            <Box component="span" sx={{ fontSize: 12, color: tone.muted }}>
              {preview.footer_line}
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {conflicts.length && onChangeCourts ? <DenseButton onClick={onChangeCourts}>Change courts</DenseButton> : null}
          <DenseButton onClick={onClose}>Not yet</DenseButton>
          <DenseButton disabled={pending} onClick={confirm} variant="primary">
            {pending ? "Opening…" : conflicts.length ? "Open around these bookings" : "Open registration"}
          </DenseButton>
        </Box>
      </SetupSurface>
    </Dialog>
  );
}
